import React from 'react';
import { TrackReference, VideoTrack } from '@livekit/components-react';
import { Pin, PinOff, MicOff, VideoOff } from 'lucide-react';

interface ParticipantCardProps {
    participant: any;
    isPinned: boolean;
    videoTrack?: TrackReference | null;
    screenShareTrack?: TrackReference | null;
    onPin: () => void;
    onUnpin: () => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
    participant,
    isPinned,
    videoTrack,
    screenShareTrack,
    onPin,
    onUnpin,
}) => {
    const isMuted = !participant.isMicrophoneEnabled;
    const isVideoOff = !participant.isCameraEnabled;
    const isScreenSharing = participant.isScreenShareEnabled;
    const isSpeaking = participant.isSpeaking;

    const displayName = participant.name || participant.identity;
    const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

    // Avatar colors - kept dark/neutral
    const avatarColors = [
        'from-gray-600 to-gray-800',
        'from-gray-700 to-gray-900',
        'from-slate-600 to-slate-800',
        'from-zinc-600 to-zinc-800',
        'from-neutral-600 to-neutral-800',
        'from-stone-600 to-stone-800',
    ];
    const colorIndex = participant.identity ? participant.identity.charCodeAt(0) % avatarColors.length : 0;
    const avatarGradient = avatarColors[colorIndex];

    const hasVideo = !!videoTrack && !isVideoOff;
    const hasScreenShare = !!screenShareTrack;

    return (
        <div
            className={`relative rounded-xl overflow-hidden bg-gray-900 group aspect-video ${isSpeaking ? 'ring-2 ring-white/50' : ''
                } transition-all duration-200`}
        >
            {/* Video or Avatar */}
            <div className="w-full h-full flex items-center justify-center relative">
                {hasVideo ? (
                    <VideoTrack
                        trackRef={videoTrack!}
                        className={`w-full h-full object-cover ${participant.isLocal ? 'scale-x-[-1]' : ''}`}
                    />
                ) : hasScreenShare ? (
                    <VideoTrack
                        trackRef={screenShareTrack!}
                        className="w-full h-full object-contain"
                    />
                ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${avatarGradient} flex items-center justify-center`}>
                        <span className="text-white text-3xl sm:text-4xl md:text-5xl font-bold">
                            {initials}
                        </span>
                    </div>
                )}
            </div>

            {/* Overlay gradient for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

            {/* Name label */}
            <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                <span className="text-white text-xs sm:text-sm font-medium bg-black/40 px-2 py-1 rounded-md backdrop-blur-sm">
                    {displayName}
                    {participant.isLocal && ' (You)'}
                </span>
            </div>

            {/* Status indicators */}
            <div className="absolute bottom-2 right-2 flex items-center gap-1">
                {isMuted && (
                    <div className="bg-black/40 backdrop-blur-sm rounded-md p-1.5">
                        <MicOff className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                    </div>
                )}
                {isVideoOff && (
                    <div className="bg-black/40 backdrop-blur-sm rounded-md p-1.5">
                        <VideoOff className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                    </div>
                )}
            </div>

            {/* Pin button (only show on hover or if pinned) */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    isPinned ? onUnpin() : onPin();
                }}
                className={`absolute top-2 right-2 p-1.5 rounded-md transition-all duration-200 ${isPinned
                    ? 'bg-white/20 text-white'
                    : 'bg-black/40 text-white opacity-0 group-hover:opacity-100 hover:bg-black/60 backdrop-blur-sm'
                    }`}
                title={isPinned ? 'Unpin participant' : 'Pin participant'}
            >
                {isPinned ? (
                    <PinOff className="w-4 h-4" />
                ) : (
                    <Pin className="w-4 h-4" />
                )}
            </button>

            {/* Screen sharing indicator */}
            {isScreenSharing && (
                <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-md font-medium flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Sharing
                </div>
            )}

            {/* Speaking indicator animation */}
            {isSpeaking && (
                <div className="absolute top-2 left-2 flex items-center gap-1">
                    <div className="flex gap-0.5">
                        <div className="w-1 h-3 bg-white/80 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                        <div className="w-1 h-4 bg-white/80 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                        <div className="w-1 h-2 bg-white/80 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParticipantCard;
